import type { CoverageChecklist } from "@/types";
import { evaluatedCoverageScope } from "@/utils/formatters";
import ViewportChip from "./ViewportChip";

type Props = {
  coverage: CoverageChecklist;
  canAddPages: boolean;
  onAddPages?: () => void;
  onWatchItem: (item: CoverageChecklist["items"][number]) => void;
};

export default function CoverageChecklistCard({ coverage, canAddPages, onAddPages, onWatchItem }: Props) {
  const {
    evaluated_pages_count: evaluatedPagesCount,
    evaluated_journeys_count: evaluatedJourneysCount = 0,
    items,
  } = coverage;
  const hasItems = items.length > 0;
  if (!hasItems) return null;

  const scope = evaluatedCoverageScope(evaluatedPagesCount, evaluatedJourneysCount);
  const subtitle = scope
    ? `From our latest pass across ${scope} · click opens that recording`
    : "From our latest pass · click opens that recording";

  return (
    <section className="dashboard-section xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <div className="dashboard-section-body flex flex-1 flex-col">
        <div className="mb-3">
          <h2 className="text-base font-semibold text-gray-900 sm:text-lg">What we checked</h2>
          {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
        </div>

        <ul className="min-h-0">
          {items.map((item) => (
            <li
              key={`${item.interaction_id}-${item.label}-${item.step_sequences.join("-")}`}
              className="first:[&>button]:border-t-0"
            >
              <button
                type="button"
                className="group grid w-full cursor-pointer grid-cols-[1rem_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-md border-t border-gray-100 px-1.5 py-2 text-left leading-snug hover:bg-primary/5"
                onClick={() => onWatchItem(item)}
              >
                <ViewportChip viewport={item.viewport} />
                <span className="min-w-0 text-sm text-gray-800 group-hover:text-primary group-hover:underline group-hover:underline-offset-[3px]">
                  {item.label}
                  {(item.source_label ?? item.page_path) ? (
                    <span className="ml-1.5 inline-block rounded bg-gray-100 px-1.5 py-px text-[11px] font-medium text-gray-500">
                      {item.source_label ?? item.page_path}
                    </span>
                  ) : null}
                </span>
                <span className="text-[11px] font-semibold tracking-wide text-primary/80">Watch</span>
              </button>
            </li>
          ))}
        </ul>

        {canAddPages && onAddPages ? (
          <div className="mt-auto flex flex-col gap-2.5 pt-3.5 sm:flex-row sm:items-center sm:gap-2.5">
            <button type="button" className="btn btn-primary w-fit" onClick={onAddPages}>
              Add pages to evaluate
            </button>
            <span className="text-sm text-gray-500">Pick more pages — we’ll browse them next</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}
